import React from 'react';
import theme from './ExtraCostRow.module.scss';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ValidatingInput, ValidatingTextArea, ValidatingSelect, ValidationScope } from '@/components/forms/validation';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { FeePreviewCompact } from '../fees';
import { ExtraCost, ChangedItem, OptionalId, ProjectFeeSet } from '@/types';
import { toOptionalNumber, toNullableNumber } from '@/utils/html';
import { Button } from '@/components/Buttons';

interface ExtraCostRowProps {
    value: OptionalId<ExtraCost>;
    onChange(item: ChangedItem<OptionalId<ExtraCost>>): void;
    onDelete(item: OptionalId<ExtraCost>): void;
    projectFeeSets: ProjectFeeSet[];
}

const ExtraCostRow: React.FC<ExtraCostRowProps> = ({ value, onChange, onDelete, projectFeeSets }) => {
    const triggerChange = (changes: Partial<ExtraCost>) => {
        triggerHandler(onChange, ChangedItem.fromMerge(value, changes));
    };

    const selectedFeeSet = value.projectFeeSetId ? projectFeeSets.find((x) => x.id === value.projectFeeSetId) : null;

    return (
        <ValidationScope>
            <div className={theme.container}>
                <div>
                    <label>
                        <Trans>Title</Trans>
                        <ValidatingInput
                            placeholder={t`Title`}
                            className="form-control"
                            fieldName={t`Title`}
                            value={value.title}
                            onChange={(e) => triggerChange({ title: e.target.value })}
                            required
                        />
                    </label>
                </div>

                <div>
                    <label>
                        <Trans>Description</Trans>
                        <ValidatingTextArea
                            placeholder={t`Description of extra cost`}
                            rows={4}
                            fieldName={t`Description`}
                            className="form-control"
                            value={value.description}
                            onChange={(e) => triggerChange({ description: e.target.value })}
                        />
                    </label>
                </div>

                <div className={theme.group}>
                    <div className={theme.amountEdit}>
                        <label>
                            <Trans>Amount</Trans>
                            <ValidatingInput
                                fieldName={t`Amount`}
                                type="number"
                                className="form-control"
                                value={value.amount}
                                onChange={(e) => triggerChange({ amount: toOptionalNumber(e.target.value) })}
                                required
                            />
                        </label>

                        <label>
                            <Trans>Fees</Trans>
                            <ValidatingSelect
                                className="form-control"
                                value={value.projectFeeSetId || ''}
                                onChange={(e) => triggerChange({ projectFeeSetId: toNullableNumber(e.target.value) })}>
                                <option value="">{t`No fees and/or VAT`}</option>
                                {projectFeeSets.map((x) => (
                                    <option key={x.id} value={x.id}>
                                        {x.name}
                                    </option>
                                ))}
                            </ValidatingSelect>
                        </label>
                    </div>

                    <div className={theme.amountPreview}>
                        <label>
                            <Trans>Final amount</Trans>
                            <FeePreviewCompact price={value.amount} quantity={1} projectFeeSet={selectedFeeSet} />
                        </label>

                        <label>
                            <Button color="danger" onClick={(e) => triggerHandler(onDelete, value)}>
                                <Trans>Remove</Trans>
                            </Button>
                        </label>
                    </div>
                </div>
                {/* 
                <div>
                    <ValidationSummary />
                </div> */}
            </div>
        </ValidationScope>
    );
};

export default ExtraCostRow;
